import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { bossBabyApi } from '@/apis/boss-baby';
import { 
  BossBabyToolDto, 
  CreateBossBabyToolDto, 
  UpdateBossBabyToolDto,
  BossBabyTaskDto,
  CreateBossBabyTaskDto,
  UpdateBossBabyTaskDto,
  BossBabySearchParams,
  UpdateBossBabyCompanySettingDto
} from '@/dto/BossBabyDto';

// ========== Tool Hook ==========
export const useBossBabyTools = () => {
  const [tools, setTools] = useState<BossBabyToolDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const fetchTools = useCallback(async (params?: BossBabySearchParams) => {
    setLoading(true);
    try {
      const result = await bossBabyApi.getTools({
        page,
        limit,
        ...params
      });
      setTools(result.list);
      setTotal(result.count);
      setPage(result.page);
      setLimit(result.limit);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch tools', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, limit, enqueueSnackbar]);

  const createTool = useCallback(async (data: CreateBossBabyToolDto) => {
    try {
      await bossBabyApi.createTool(data);
      enqueueSnackbar('Tool created successfully', { variant: 'success' });
      navigate('/boss-baby/tools');
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to create tool', {
        variant: 'error'
      });
      throw error;
    }
  }, [navigate, enqueueSnackbar]);

  const updateTool = useCallback(async (
    toolId: number, 
    data: UpdateBossBabyToolDto
  ) => {
    try {
      await bossBabyApi.updateTool(toolId, data);
      enqueueSnackbar('Tool updated successfully', { variant: 'success' });
      navigate('/boss-baby/tools');
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update tool', {
        variant: 'error'
      });
      throw error;
    }
  }, [navigate, enqueueSnackbar]);

  const deleteTool = useCallback(async (toolId: number) => {
    try {
      await bossBabyApi.deleteTool(toolId);
      enqueueSnackbar('Tool deleted successfully', { variant: 'success' });
      await fetchTools();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to delete tool', {
        variant: 'error'
      });
    }
  }, [fetchTools, enqueueSnackbar]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  return {
    tools,
    loading,
    total,
    page,
    limit,
    fetchTools,
    createTool,
    updateTool,
    deleteTool,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

// ========== Task Hook ==========
export const useBossBabyTasks = () => {
  const [tasks, setTasks] = useState<BossBabyTaskDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const fetchTasks = useCallback(async (params?: BossBabySearchParams) => {
    setLoading(true);
    try {
      const result = await bossBabyApi.getTasks({
        page,
        limit,
        ...params
      });
      setTasks(result.list);
      setTotal(result.count);
      setPage(result.page);
      setLimit(result.limit);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch tasks', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, limit, enqueueSnackbar]);

  const createTask = useCallback(async (data: CreateBossBabyTaskDto) => {
    try {
      await bossBabyApi.createTask(data);
      enqueueSnackbar('Task created successfully', { variant: 'success' });
      navigate('/boss-baby/tasks');
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to create task', {
        variant: 'error'
      });
      throw error;
    }
  }, [navigate, enqueueSnackbar]);

  const updateTask = useCallback(async (
    taskId: number, 
    data: UpdateBossBabyTaskDto
  ) => {
    try {
      await bossBabyApi.updateTask(taskId, data);
      enqueueSnackbar('Task updated successfully', { variant: 'success' });
      navigate('/boss-baby/tasks');
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update task', {
        variant: 'error'
      });
      throw error;
    }
  }, [navigate, enqueueSnackbar]);

  const deleteTask = useCallback(async (taskId: number) => {
    try {
      await bossBabyApi.deleteTask(taskId);
      enqueueSnackbar('Task deleted successfully', { variant: 'success' });
      await fetchTasks();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to delete task', {
        variant: 'error'
      });
    }
  }, [fetchTasks, enqueueSnackbar]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  return {
    tasks,
    loading,
    total,
    page,
    limit,
    fetchTasks,
    createTask,
    updateTask,
    deleteTask,
    handlePageChange,
    handleRowsPerPageChange,
  };
};

// ========== Tool Detail Hook ==========
export const useBossBabyToolDetail = (toolId: number | string) => {
  const [tool, setTool] = useState<BossBabyToolDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchTool = useCallback(async () => {
    if (!toolId) return;
    
    setLoading(true);
    try {
      const result = await bossBabyApi.getTool(Number(toolId));
      setTool(result);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch tool', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [toolId, enqueueSnackbar]);

  return {
    tool,
    loading,
    fetchTool,
  };
};

// ========== Task Detail Hook ==========
export const useBossBabyTaskDetail = (taskId: number | string) => {
  const [task, setTask] = useState<BossBabyTaskDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    
    setLoading(true);
    try {
      const result = await bossBabyApi.getTask(Number(taskId));
      setTask(result);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch task', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [taskId, enqueueSnackbar]);

  return {
    task,
    loading,
    fetchTask,
  };
};

// ========== Company Settings Hook ==========
export const useBossBabyCompanySettings = () => {
  const [settings, setSettings] = useState<{
    tools: Array<BossBabyToolDto & { companySetting?: any }>;
    tasks: Array<BossBabyTaskDto & { companySetting?: any }>;
  }>({ tools: [], tasks: [] });
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchCompanySettings = useCallback(async (comId: number) => {
    setLoading(true);
    try {
      const result = await bossBabyApi.getCompanySettings(comId);
      setSettings(result);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch company settings', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const updateToolUsage = useCallback(async (
    comId: number,
    toolId: number,
    data: UpdateBossBabyCompanySettingDto
  ) => {
    try {
      await bossBabyApi.updateCompanyToolUsage(comId, toolId, data);
      enqueueSnackbar('Tool setting updated successfully', { variant: 'success' });
      await fetchCompanySettings(comId);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update tool setting', {
        variant: 'error'
      });
    }
  }, [fetchCompanySettings, enqueueSnackbar]);

  const updateTaskUsage = useCallback(async (
    comId: number,
    taskId: number,
    data: UpdateBossBabyCompanySettingDto
  ) => {
    try {
      await bossBabyApi.updateCompanyTaskUsage(comId, taskId, data);
      enqueueSnackbar('Task setting updated successfully', { variant: 'success' });
      await fetchCompanySettings(comId);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to update task setting', {
        variant: 'error'
      });
    }
  }, [fetchCompanySettings, enqueueSnackbar]);

  return {
    settings,
    loading,
    fetchCompanySettings,
    updateToolUsage,
    updateTaskUsage,
  };
};

// ========== Utility Hook ==========
export const useBossBabyUtils = () => {
  const [activeTools, setActiveTools] = useState<BossBabyToolDto[]>([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchActiveTools = useCallback(async () => {
    setLoading(true);
    try {
      const result = await bossBabyApi.getAllActiveTools();
      setActiveTools(result);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to fetch active tools', {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const validateJsonSchema = useCallback((schema: string): boolean => {
    try {
      const parsed = JSON.parse(schema);
      return typeof parsed === 'object' && parsed !== null;
    } catch {
      return false;
    }
  }, []);

  const validateTaskConfig = useCallback((config: string): boolean => {
    try {
      const parsed = JSON.parse(config);
      return typeof parsed === 'object' && parsed !== null;
    } catch {
      return false;
    }
  }, []);

  return {
    activeTools,
    loading,
    fetchActiveTools,
    validateJsonSchema,
    validateTaskConfig,
  };
};